import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Loader2, Inbox, ShoppingBag, DollarSign } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/data/content";

const StatCard = ({ icon: Icon, label, value, tint }) => (
  <div data-testid={`stat-${label}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-sm text-gray-400">{label}</div>
        <div className="font-display font-bold text-3xl text-white mt-1">{value}</div>
      </div>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tint}`}>
        <Icon size={22} />
      </div>
    </div>
  </div>
);

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const o = await api.get("/payments/orders");
      setOrders(o.data);
    } catch (e) {
      toast.error("Failed to load orders.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const paidRevenue = orders
    .filter((o) => o.payment_status === "paid")
    .reduce((a, o) => a + (o.amount || 0) / 100, 0);

  return (
    <div className="min-h-screen bg-[#0A1428] text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0A1428]/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt={BRAND.name} className="h-10 w-10 rounded-lg object-contain" />
            <div>
              <div className="font-display font-bold text-lg leading-none">TITAN<span className="text-[#1E6BFF]">.</span></div>
              <div className="text-xs text-gray-500 mt-0.5">Admin dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-gray-400">{user?.email}</span>
            <button
              onClick={doLogout}
              data-testid="logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-white/40 px-4 py-2 text-sm font-medium transition-colors"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8 max-w-2xl">
          <StatCard icon={ShoppingBag} label="Orders" value={orders.length} tint="bg-white/10 text-white" />
          <StatCard icon={DollarSign} label="Paid revenue" value={`$${paidRevenue.toLocaleString()}`} tint="bg-emerald-500/15 text-emerald-400" />
        </div>

        {/* Orders */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-2xl text-white">Orders</h2>
            <span className="text-sm text-gray-500" data-testid="orders-count">
              {orders.length} total · ${paidRevenue.toLocaleString()} paid
            </span>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
            {loading ? (
              <div className="py-24 flex items-center justify-center text-gray-400 gap-2">
                <Loader2 className="animate-spin" size={20} /> Loading orders…
              </div>
            ) : orders.length === 0 ? (
              <div className="py-16 flex flex-col items-center justify-center text-gray-500 gap-3" data-testid="orders-empty">
                <Inbox size={32} />
                <p className="text-sm">No orders yet. Share your pricing page to start selling.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 uppercase text-xs tracking-wider border-b border-white/10">
                      <th className="px-5 py-4 font-medium">Customer</th>
                      <th className="px-5 py-4 font-medium">Package</th>
                      <th className="px-5 py-4 font-medium">Amount</th>
                      <th className="px-5 py-4 font-medium">Status</th>
                      <th className="px-5 py-4 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr key={o.session_id} data-testid={`order-row-${o.session_id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                        <td className="px-5 py-4">
                          <div className="text-white">{o.customer_name || "—"}</div>
                          <div className="text-gray-500 text-xs">{o.customer_email || "—"}</div>
                        </td>
                        <td className="px-5 py-4 text-gray-300">{o.lookup_key?.replace("titan_", "").replace(/^\w/, (c) => c.toUpperCase())}</td>
                        <td className="px-5 py-4 font-medium text-white">${((o.amount || 0) / 100).toLocaleString()}</td>
                        <td className="px-5 py-4">
                          <span className={`inline-block text-xs font-semibold px-3 py-1 rounded-full border ${
                            o.payment_status === "paid"
                              ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                              : o.payment_status === "pending"
                              ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                              : "bg-rose-500/15 text-rose-400 border-rose-500/30"
                          }`}>
                            {o.payment_status}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-gray-400">
                          {o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
